import { ReportHandler, Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';

const reportWebVitals = (onPerfEntry?: ReportHandler) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    });
  }
};

// Send Web Vitals to Sentry
export const sendWebVitalsToSentry = () => {
  const sendToSentry = (metric: Metric) => {
    // Only send metrics if Sentry is initialized and we have a DSN
    if (!process.env.REACT_APP_SENTRY_DSN) {
      return;
    }

    // Skip web vitals from bots/crawlers (HeadlessChrome, Lighthouse, etc.)
    const ua = navigator.userAgent || '';
    if (/HeadlessChrome|Lighthouse|Googlebot|bingbot|Baiduspider/i.test(ua)) {
      return;
    }

    // Define thresholds for each metric — only alert on truly poor values
    // (Google "poor" thresholds, not "needs improvement")
    const thresholds: Record<string, number> = {
      CLS: 0.25,    // Cumulative Layout Shift (poor: >0.25)
      FID: 300,     // First Input Delay (poor: >300ms)
      FCP: 3000,    // First Contentful Paint (poor: >3000ms)
      LCP: 4000,    // Largest Contentful Paint (poor: >4000ms)
      TTFB: 1800,   // Time to First Byte (poor: >1800ms)
    };

    // Send all metrics as custom context
    Sentry.setContext('web_vitals', {
      [metric.name]: {
        value: metric.value,
        delta: metric.delta,
        id: metric.id,
      },
    });

    // For poor performing metrics, send as events for visibility (10% sample)
    if (metric.value > (thresholds[metric.name] || Infinity) && Math.random() < 0.1) {
      Sentry.captureMessage(
        `Poor ${metric.name} detected: ${metric.value}${metric.name === 'CLS' ? '' : 'ms'}`,
        {
          level: 'warning',
          tags: {
            webVital: metric.name,
          },
          contexts: {
            webVitals: {
              value: metric.value,
              delta: metric.delta,
              id: metric.id,
            },
          },
        }
      );
    }
  };

  reportWebVitals(sendToSentry);
};

export default reportWebVitals;
